import * as React from 'react';

import {
  Button,
  Checkbox,
  ContentCard,
  Empty,
  Label,
  Loading,
  Popover,
  PopoverDialog,
  PopoverTrigger,
} from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';
import { ChevronDown, ChevronUp, Download, Search } from 'lucide-react';

import {
  $generateFileAccessURL,
  $remoteBookingLoads,
} from '../../_state/bookingDetail.state';
import { useCreateZipArchive } from '../../hooks/useCreateZipArchive';
import { LoadRecord } from '../../types/load';

import { PhotoGalleryDialog } from './BookingTabPhotosGalleryDialog';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { BookingRecord } from '~/modules/trade/types/booking';
import { useDebouncedValue } from '~/shared/hooks/useDebounce';

interface Props {
  booking: BookingRecord;
}

const PHOTO_TYPE_OPTIONS = [
  { key: 'load', label: 'Load' },
  { key: 'materials', label: 'Materials' },
] as const;
type PhotoType = (typeof PHOTO_TYPE_OPTIONS)[number]['key'];

// Helper function to generate unique filename with timestamp
const generateUniqueFilename = (baseName: string) => {
  const timestamp = Date.now();
  const hash = Math.random().toString(36).substring(2, 8);
  return `${baseName}-${timestamp}-${hash}.zip`;
};

export const BookingTabPhotos: React.FC<Props> = ({ booking }) => {
  const t = useTranslations();
  const [searchTerm, setSearchTerm] = React.useState('');
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);

  // Fetch all loads for this booking with photos
  const { data: loadsData, isLoading } = useAtomValue($remoteBookingLoads);

  const allFileIds = React.useMemo(
    () =>
      loadsData?.loads
        ?.flatMap((load) => load.files?.map((file) => file.id))
        ?.filter((id): id is string => !!id) ?? [],
    [loadsData],
  );

  const generateDownloadUrl = useSetAtom($generateFileAccessURL);
  const { createAndDownloadZip } = useCreateZipArchive();

  // State for storing download URLs
  const [downloadUrlByLoadId, setDownloadUrlByLoadId] = React.useState<
    Record<string, string>
  >({});
  const [isLoadingUrls, setIsLoadingUrls] = React.useState(false);

  // Load download URLs when file IDs change
  React.useEffect(() => {
    if (allFileIds.length === 0) {
      setDownloadUrlByLoadId({});
      return;
    }

    setIsLoadingUrls(true);
    generateDownloadUrl({ fileIds: allFileIds })
      .then((results) => {
        const urlMap =
          results.filesGenerateFileAccessURL?.results?.reduce(
            (acc, result) => {
              if (result.fileId && result.url) {
                acc[result.fileId] = result.url;
              }
              return acc;
            },
            {} as Record<string, string>,
          ) ?? {};
        setDownloadUrlByLoadId(urlMap);
      })
      .catch((error) => {
        console.error('Failed to generate download URLs:', error);
      })
      .finally(() => {
        setIsLoadingUrls(false);
      });
  }, [allFileIds, generateDownloadUrl]);

  // Filter loads based on search term
  const filteredLoads = React.useMemo(() => {
    const loads = loadsData?.loads || [];
    if (!debouncedSearchTerm) return loads;

    const normalizedSearchTerm = debouncedSearchTerm.toLowerCase().trim();

    return loads.filter((load) => {
      // Check load number
      if (load.loadNumber?.toLowerCase().includes(normalizedSearchTerm)) {
        return true;
      }

      // Check photos
      if (load.files) {
        const hasMatchingPhoto = load.files.some(
          (file) =>
            file.fileName?.toLowerCase().includes(normalizedSearchTerm) ||
            file.entityType?.toLowerCase().includes(normalizedSearchTerm) ||
            file.entityId?.toLowerCase().includes(normalizedSearchTerm),
        );
        if (hasMatchingPhoto) return true;
      }

      return false;
    });
  }, [loadsData, debouncedSearchTerm]);

  // Collect all loads with at least one photo (of any type) and defined id
  const loadsWithPhotos = React.useMemo(
    () =>
      (loadsData?.loads || [])
        .filter(
          (load): load is LoadRecord =>
            (load.files?.length ?? 0) > 0 && typeof load.id === 'string',
        )
        .map((load) => ({ ...load, id: load.id as string })),
    [loadsData],
  );

  const hasPhotos = loadsWithPhotos.length > 0;

  // --- Download All Popover State ---
  const [selectedPhotoTypes, setSelectedPhotoTypes] = React.useState<
    PhotoType[]
  >(['load', 'materials']);
  const [selectedLoadIds, setSelectedLoadIds] = React.useState<string[]>([]);
  const [isDownloadingAll, setIsDownloadingAll] = React.useState(false);

  // Photo Gallery Dialog State
  const [isGalleryOpen, setIsGalleryOpen] = React.useState(false);
  const [selectedPhotoId, setSelectedPhotoId] = React.useState<
    string | undefined
  >();

  // Helper: get fileIds for selected loads and photo types
  const getSelectedFileIds = () => {
    const fileIds: string[] = [];
    for (const load of loadsWithPhotos) {
      if (!selectedLoadIds.includes(load.id)) continue;
      for (const file of load.files || []) {
        if (typeof file.id === 'string') fileIds.push(file.id);
      }
    }
    return fileIds;
  };

  // Download all handler
  const handleDownloadAll = async () => {
    setIsDownloadingAll(true);
    try {
      const fileIds = getSelectedFileIds();
      if (fileIds.length > 0) {
        const uniqueFilename = generateUniqueFilename(
          `booking-${booking.bookingNumber}-photos`,
        );
        await createAndDownloadZip(fileIds, uniqueFilename);
      }
    } catch (error) {
      console.error('Failed to download all photos:', error);
    } finally {
      setIsDownloadingAll(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-4">
        <Loading />
      </div>
    );
  }

  return hasPhotos ? (
    <div className="@BookingPhotos p-5">
      {/* Search and Download Controls */}
      <ContentCard className="mb-6">
        <div className="flex flex-row items-center justify-between">
          <div className="flex h-[40px] w-[372px] flex-row items-center gap-[10px] rounded-md border border-neutral/20 p-2">
            <Search className="text-neutral/60" size={16} />
            <input
              className="flex-1 border-none bg-transparent text-sm outline-none placeholder:text-neutral/60"
              placeholder={t.bookingDetail.photos.searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Download all popover */}
          <PopoverTrigger>
            <Button variant="outline" size="large" icon={<Download />}>
              {t.bookingDetail.photos.downloadAll}
            </Button>
            <Popover className="mr-4">
              <PopoverDialog>
                <DownloadAllPhotosPopover
                  loads={loadsWithPhotos}
                  selectedPhotoTypes={selectedPhotoTypes}
                  setSelectedPhotoTypes={setSelectedPhotoTypes}
                  selectedLoadIds={selectedLoadIds}
                  setSelectedLoadIds={setSelectedLoadIds}
                  isDownloading={isDownloadingAll}
                  onDownload={handleDownloadAll}
                />
              </PopoverDialog>
            </Popover>
          </PopoverTrigger>
        </div>
      </ContentCard>

      {/* Photo Content */}
      <ContentCard>
        <div className="flex flex-col gap-4">
          {filteredLoads.length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-neutral/50">
                {searchTerm
                  ? t.bookingDetail.photos.noSearchResults
                  : t.bookingDetail.photos.noLoads}
              </p>
            </div>
          ) : (
            filteredLoads.map((load) => (
              <LoadPhotoRowComponent
                key={load.id}
                load={load}
                downloadUrlByLoadId={downloadUrlByLoadId}
                isLoadingUrls={isLoadingUrls}
                onDownloadLoad={createAndDownloadZip}
                onPhotoClick={(photoId) => {
                  setSelectedPhotoId(photoId);
                  setIsGalleryOpen(true);
                }}
              />
            ))
          )}
        </div>

        {/* Photo Gallery Dialog */}
        <PhotoGalleryDialog
          isOpen={isGalleryOpen}
          onClose={() => {
            setIsGalleryOpen(false);
            setSelectedPhotoId(undefined);
          }}
          loads={loadsWithPhotos}
          downloadUrlByLoadId={downloadUrlByLoadId}
          initialPhotoId={selectedPhotoId}
        />
      </ContentCard>
    </div>
  ) : (
    <div className="flex h-full items-center justify-center">
      <Empty
        className="h-full"
        title={t.bookingDetail.photos.noPhotos}
        centered={true}
      />
    </div>
  );
};

// --- DownloadAllPhotosPopover Component ---
interface DownloadAllPhotosPopoverProps {
  loads: Array<Partial<LoadRecord> & { id: string }>;
  selectedPhotoTypes: PhotoType[];
  setSelectedPhotoTypes: (types: PhotoType[]) => void;
  selectedLoadIds: string[];
  setSelectedLoadIds: (ids: string[]) => void;
  isDownloading: boolean;
  onDownload: () => void;
}

const DownloadAllPhotosPopover: React.FC<DownloadAllPhotosPopoverProps> = ({
  loads,
  selectedPhotoTypes,
  setSelectedPhotoTypes,
  selectedLoadIds,
  setSelectedLoadIds,
  isDownloading,
  onDownload,
}) => {
  const t = useTranslations();

  // Helper: is a load selectable for the current photo types?
  const isLoadSelectable = (load: Partial<LoadRecord>) => {
    if (!load.files) return false;
    return load.files.length > 0;
  };

  // Handlers
  const handlePhotoTypeChange = (type: PhotoType) => {
    if (selectedPhotoTypes.includes(type)) {
      setSelectedPhotoTypes(selectedPhotoTypes.filter((t) => t !== type));
    } else {
      setSelectedPhotoTypes([...selectedPhotoTypes, type]);
    }
  };

  const handleLoadChange = (id: string) => {
    if (selectedLoadIds.includes(id)) {
      setSelectedLoadIds(selectedLoadIds.filter((l) => l !== id));
    } else {
      setSelectedLoadIds([...selectedLoadIds, id]);
    }
  };

  return (
    <div className="@DownloadAllPhotosPopover min-w-[340px] p-4">
      <Label aria-required className="text-sm font-semibold">
        {t.bookingDetail.photos.photoTypes}
      </Label>
      <div className="my-2 grid grid-cols-2 gap-4">
        {PHOTO_TYPE_OPTIONS.map((option) => (
          <Checkbox
            isSelected={selectedPhotoTypes.includes(option.key)}
            onChange={() => handlePhotoTypeChange(option.key)}
            className="text-sm"
          >
            {t.bookingDetail.photos[option.label.toLowerCase() as never] ||
              option.label}
          </Checkbox>
        ))}
      </div>
      <Label aria-required className="text-sm font-semibold">
        {t.bookingDetail.photos.selectLoads}
      </Label>
      <div className="my-2 grid grid-cols-2 gap-4">
        {loads.map((load) => {
          const selectable = isLoadSelectable(load);
          return (
            <Checkbox
              key={load.id}
              isSelected={selectedLoadIds.includes(load.id)}
              onChange={() => handleLoadChange(load.id)}
              isDisabled={!selectable}
              className="text-sm"
            >
              {t.bookingDetail.photos.load} {load.loadNumber}
            </Checkbox>
          );
        })}
      </div>
      <Button
        variant="outline"
        icon={<Download />}
        onPress={onDownload}
        isDisabled={
          selectedLoadIds.length === 0 ||
          selectedPhotoTypes.length === 0 ||
          isDownloading
        }
        className="mt-2 w-full"
      >
        {isDownloading
          ? t.bookingDetail.photos.downloading
          : t.bookingDetail.photos.download}
      </Button>
    </div>
  );
};

// Component for individual load photo row
const LoadPhotoRowComponent: React.FC<{
  load: Partial<LoadRecord>;
  downloadUrlByLoadId: Record<string, string>;
  isLoadingUrls: boolean;
  onDownloadLoad: (fileIds: string[], archiveName: string) => Promise<void>;
  onPhotoClick: (photoId: string) => void;
}> = ({
  load,
  downloadUrlByLoadId,
  isLoadingUrls,
  onDownloadLoad,
  onPhotoClick,
}) => {
  const t = useTranslations();
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [isDownloading, setIsDownloading] = React.useState(false);

  const loadNumber = load.loadNumber || 'N/A';

  // Separate load photos and material photos
  const loadPhotos = load.files ?? [];
  // TODO: When material photos are implemented, add them here
  const materialPhotos: LoadRecord['files'] = [];

  const hasPhotos = loadPhotos.length > 0 || materialPhotos.length > 0;

  const handleDownload = (url: string) => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  const handleLoadDownload = async () => {
    if (loadPhotos.length === 0) {
      return;
    }

    setIsDownloading(true);
    try {
      const fileIds = loadPhotos
        .map((photo) => photo.id)
        .filter((id): id is string => !!id);

      if (fileIds.length > 0) {
        const uniqueFilename = generateUniqueFilename(
          `load-${loadNumber}-photos`,
        );
        await onDownloadLoad(fileIds, uniqueFilename);
      }
    } catch (error) {
      console.error('Failed to download load photos:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="@LoadPhotoRowComponent relative flex w-full flex-row items-center justify-between">
      {/* Download menu button positioned absolutely */}
      <div className="absolute top-0 right-0 z-10">
        <Button
          variant="outline"
          size="default"
          icon={<Download />}
          onPress={handleLoadDownload}
          isDisabled={loadPhotos.length === 0 || isDownloading}
          className="w-[120px]"
        >
          {isDownloading
            ? t.bookingDetail.photos.downloading
            : t.bookingDetail.photos.download}
        </Button>
      </div>

      {/* Collapsible load section */}
      <div className="w-full">
        <div
          className="flex cursor-pointer items-center gap-2 py-2"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex size-[44px] items-center justify-center rounded-[6px] bg-neutral/5 p-[10px]">
            {isExpanded ? (
              <ChevronDown className="size-4 text-neutral/60" />
            ) : (
              <ChevronUp className="size-4 text-neutral/60" />
            )}
          </div>
          <div className="text-lg font-bold">
            {t.bookingDetail.photos.load} {loadNumber}
          </div>
        </div>

        {isExpanded && (
          <div className="mt-[20px] flex flex-col gap-[20px] pl-[56px]">
            {!hasPhotos ? (
              <div className="py-8 text-center text-neutral/50">
                {t.bookingDetail.photos.noPhotos}
              </div>
            ) : (
              <>
                {/* Load Photos Section */}
                {loadPhotos.length > 0 && (
                  <div className="flex flex-col gap-[10px]">
                    <div className="text-base font-semibold">
                      {t.bookingDetail.photos.loadPhotos}
                    </div>
                    <div className="grid grid-cols-4 gap-3">
                      {loadPhotos.map((photo) => (
                        <PhotoItem
                          key={photo.id}
                          photo={photo}
                          url={downloadUrlByLoadId[photo.id]}
                          isLoadingUrls={isLoadingUrls}
                          onDownload={() =>
                            handleDownload(downloadUrlByLoadId[photo.id])
                          }
                          onPhotoClick={() => onPhotoClick(photo.id)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Material Photos Section */}
                {materialPhotos.length > 0 && (
                  <div className="flex flex-col gap-[10px]">
                    <div className="text-base font-semibold">
                      {t.bookingDetail.photos.materialPhotos}
                    </div>
                    <div className="grid grid-cols-4 gap-3">
                      {materialPhotos.map((photo) => (
                        <PhotoItem
                          key={photo.id}
                          photo={photo}
                          url={downloadUrlByLoadId[photo.id]}
                          isLoadingUrls={isLoadingUrls}
                          onDownload={() =>
                            handleDownload(downloadUrlByLoadId[photo.id])
                          }
                          onPhotoClick={() => onPhotoClick(photo.id)}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Individual photo item component
const PhotoItem: React.FC<{
  photo: NonNullable<LoadRecord['files']>[number];
  url: string | undefined;
  isLoadingUrls: boolean;
  onDownload: () => void;
  onPhotoClick: () => void;
}> = ({ photo, url, isLoadingUrls, onDownload, onPhotoClick }) => {
  const t = useTranslations();

  return (
    <div className="@PhotoItem flex flex-col gap-2">
      {/* Photo container */}
      <div className="group relative aspect-square overflow-hidden rounded-lg border bg-neutral/10">
        {isLoadingUrls ? (
          <div className="flex size-full items-center justify-center">
            <span className="text-xs text-neutral/50">
              {t.bookingDetail.photos.loading}
            </span>
          </div>
        ) : url ? (
          <img
            src={url}
            alt={photo.fileName}
            className="size-full cursor-pointer object-cover"
            onClick={onPhotoClick}
          />
        ) : (
          <div className="flex size-full items-center justify-center">
            <span className="text-xs text-neutral/50">
              {t.bookingDetail.photos.noImage}
            </span>
          </div>
        )}

        {/* Download button overlay */}
        <Button
          variant="ghost"
          size="icon"
          icon={<Download />}
          onPress={onDownload}
          className="absolute top-2 right-2 size-8 rounded-full bg-white/90 opacity-0 shadow-sm transition-opacity group-hover:opacity-100 hover:bg-white"
        />
      </div>

      {/* Photo info */}
      <div className="space-y-1 text-xs">
        <p className="truncate font-medium">
          {photo.metadata?.notes ?? photo.fileName}
        </p>
      </div>
    </div>
  );
};
